const NAME_RELEASE_MS = 5_000;

let liveRelease: (() => void) | null = null;

export function focusMainTransiently(announcement?: string): boolean {
  const main = document.querySelector("main");
  if (main === null) return false;
  liveRelease?.();
  const naming = announcement !== undefined && !main.hasAttribute("aria-labelledby");
  const previousLabel = main.getAttribute("aria-label");
  let nameTimer: ReturnType<typeof setTimeout> | undefined;
  const releaseName = () => {
    if (nameTimer !== undefined) clearTimeout(nameTimer);
    document.removeEventListener("click", releaseName, true);
    if (!naming) return;
    if (previousLabel === null) main.removeAttribute("aria-label");
    else main.setAttribute("aria-label", previousLabel);
  };
  const release = () => {
    if (liveRelease === release) liveRelease = null;
    main.removeEventListener("blur", release);
    main.removeEventListener("pointerdown", release, true);
    main.removeAttribute("tabindex");
    releaseName();
  };
  main.tabIndex = -1;
  if (naming) main.setAttribute("aria-label", announcement);
  main.focus();
  if (document.activeElement !== main) {
    release();
    return false;
  }
  liveRelease = release;
  main.addEventListener("blur", release, { once: true });
  main.addEventListener("pointerdown", release, { once: true, capture: true });
  main.addEventListener(
    "click",
    () => {
      if (document.activeElement === main) main.blur();
    },
    { once: true, capture: true },
  );
  document.addEventListener("click", releaseName, { once: true, capture: true });
  if (naming) nameTimer = setTimeout(releaseName, NAME_RELEASE_MS);
  return announcement === undefined || naming;
}

export function focusMainWhenRevealed(announcement: string): () => void {
  const cover = document.querySelector("main")?.closest("[hidden]");
  const active = document.activeElement;
  if (cover == null || (active !== null && active !== document.body)) return () => {};
  const armedAt = Date.now();
  const landOnReveal = () => {
    if (document.querySelector("main")?.closest("[hidden]") != null) return;
    stop();
    const stillSpeaking = Date.now() - armedAt < NAME_RELEASE_MS;
    focusMainTransiently(stillSpeaking ? announcement : undefined);
  };
  const observer = new MutationObserver(landOnReveal);
  const stop = () => {
    observer.disconnect();
    document.removeEventListener("focusin", stop, true);
  };
  observer.observe(cover, { attributes: true, attributeFilter: ["hidden"] });
  document.addEventListener("focusin", stop, true);
  return stop;
}
